import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Snackbar,
  Divider,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import {
  Save as SaveIcon,
  Notifications as NotificationsIcon,
  Email as EmailIcon,
  PlayArrow as PlayIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { configService } from '../services/configService';

function Configuration() {
  const [config, setConfig] = useState({
    pendingSignatureThreshold: 20,
    pendingSignatureEmails: [''],
    pendingTransferThreshold: 15,
    pendingTransferEmails: [''],
    pendingPaymentThreshold: 15,
    pendingPaymentEmails: [''],
    executionTime: '09:00',
    isActive: true
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const result = await configService.getNotificationConfig();
      
      if (result.success && result.data) {
        // Convertir isActive de número a booleano si es necesario
        // Convertir emails de string a array si es necesario (retrocompatibilidad)
        const configData = {
          pendingSignatureThreshold: result.data.pendingSignatureThreshold || 20,
          pendingTransferThreshold: result.data.pendingTransferThreshold || 15,
          pendingPaymentThreshold: result.data.pendingPaymentThreshold || 15,
          executionTime: result.data.executionTime || '09:00',
          isActive: Boolean(result.data.isActive),
          pendingSignatureEmails: Array.isArray(result.data.pendingSignatureEmails) 
            ? (result.data.pendingSignatureEmails.length > 0 ? result.data.pendingSignatureEmails : [''])
            : (result.data.pendingSignatureEmail ? [result.data.pendingSignatureEmail] : ['']),
          pendingTransferEmails: Array.isArray(result.data.pendingTransferEmails)
            ? (result.data.pendingTransferEmails.length > 0 ? result.data.pendingTransferEmails : [''])
            : (result.data.pendingTransferEmail ? [result.data.pendingTransferEmail] : ['']),
          pendingPaymentEmails: Array.isArray(result.data.pendingPaymentEmails)
            ? (result.data.pendingPaymentEmails.length > 0 ? result.data.pendingPaymentEmails : [''])
            : (result.data.pendingPaymentEmail ? [result.data.pendingPaymentEmail] : [''])
        };
        setConfig(configData);
      } else {
        showSnackbar('Error al cargar configuración: ' + (result.message || 'No se encontró configuración'), 'error');
      }
    } catch (error) {
      console.error('Error al cargar configuración:', error);
      showSnackbar('Error al cargar configuración', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    
    // Limpiar error del campo
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleEmailChange = (field, index, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: prev[field].map((email, i) => i === index ? value : email)
    }));
    
    // Limpiar error del campo
    const errorKey = `${field}[${index}]`;
    if (errors[errorKey]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const handleAddEmail = (field) => {
    setConfig(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const handleRemoveEmail = (field, index) => {
    setConfig(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
    
    // Limpiar error del campo eliminado
    const errorKey = `${field}[${index}]`;
    if (errors[errorKey]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validar umbrales
    if (config.pendingSignatureThreshold < 1) {
      newErrors.pendingSignatureThreshold = 'Debe ser mayor a 0';
    }
    if (config.pendingTransferThreshold < 1) {
      newErrors.pendingTransferThreshold = 'Debe ser mayor a 0';
    }
    if (config.pendingPaymentThreshold < 1) {
      newErrors.pendingPaymentThreshold = 'Debe ser mayor a 0';
    }
    
    // Validar emails de pendingSignatureEmails
    const validEmails = (config.pendingSignatureEmails || ['']).filter(email => email.trim() !== '');
    if (validEmails.length === 0) {
      newErrors.pendingSignatureEmails = 'Debe agregar al menos un email';
    } else {
      (config.pendingSignatureEmails || ['']).forEach((email, index) => {
        if (email.trim() !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          newErrors[`pendingSignatureEmails[${index}]`] = 'Email inválido';
        }
      });
    }
    
    // Validar emails de pendingTransferEmails
    const validTransferEmails = (config.pendingTransferEmails || ['']).filter(email => email.trim() !== '');
    if (validTransferEmails.length === 0) {
      newErrors.pendingTransferEmails = 'Debe agregar al menos un email';
    } else {
      (config.pendingTransferEmails || ['']).forEach((email, index) => {
        if (email.trim() !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          newErrors[`pendingTransferEmails[${index}]`] = 'Email inválido';
        }
      });
    }
    
    // Validar emails de pendingPaymentEmails
    const validPaymentEmails = (config.pendingPaymentEmails || ['']).filter(email => email.trim() !== '');
    if (validPaymentEmails.length === 0) {
      newErrors.pendingPaymentEmails = 'Debe agregar al menos un email';
    } else {
      (config.pendingPaymentEmails || ['']).forEach((email, index) => {
        if (email.trim() !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          newErrors[`pendingPaymentEmails[${index}]`] = 'Email inválido';
        }
      });
    }
    
    if (!config.executionTime || !/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(config.executionTime)) {
      newErrors.executionTime = 'Formato de hora inválido (HH:mm)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showSnackbar('Por favor, corrija los errores en el formulario', 'error');
      return;
    }
    
    setSaving(true);
    try {
      // Limpiar emails vacíos
      const pendingSignatureEmailsClean = (config.pendingSignatureEmails || ['']).filter(email => email.trim() !== '');
      const pendingTransferEmailsClean = (config.pendingTransferEmails || ['']).filter(email => email.trim() !== '');
      const pendingPaymentEmailsClean = (config.pendingPaymentEmails || ['']).filter(email => email.trim() !== '');
      
      // Asegurar que isActive sea booleano y preparar configuración
      // Enviar tanto el formato de arrays (nuevo) como el singular (retrocompatibilidad)
      const configToSave = {
        pendingSignatureThreshold: config.pendingSignatureThreshold,
        pendingSignatureEmail: pendingSignatureEmailsClean[0] || '', // Primer email para retrocompatibilidad
        pendingSignatureEmails: pendingSignatureEmailsClean,
        pendingTransferThreshold: config.pendingTransferThreshold,
        pendingTransferEmail: pendingTransferEmailsClean[0] || '', // Primer email para retrocompatibilidad
        pendingTransferEmails: pendingTransferEmailsClean,
        pendingPaymentThreshold: config.pendingPaymentThreshold,
        pendingPaymentEmail: pendingPaymentEmailsClean[0] || '', // Primer email para retrocompatibilidad
        pendingPaymentEmails: pendingPaymentEmailsClean,
        executionTime: config.executionTime,
        isActive: Boolean(config.isActive)
      };
      
      const result = await configService.updateNotificationConfig(configToSave);
      
      if (result.success) {
        showSnackbar('Configuración guardada correctamente', 'success');
        // Recargar configuración desde el servidor para mantener sincronización
        loadConfig();
      } else {
        showSnackbar('Error al guardar configuración: ' + result.message, 'error');
      }
    } catch (error) {
      showSnackbar('Error al guardar configuración', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTestNotifications = async () => {
    setTesting(true);
    try {
      const result = await configService.checkNotifications();
      
      if (result.success) {
        const { notificationsSent, counts } = result.data;
        
        if (notificationsSent > 0) {
          showSnackbar(
            `Se enviaron ${notificationsSent} notificación(es). Estados: Firma: ${counts.pendingSignature}, Transferencia: ${counts.pendingTransfer}, Pago: ${counts.pendingPayment}`,
            'success'
          );
        } else {
          showSnackbar(
            `No se enviaron notificaciones. Estados: Firma: ${counts.pendingSignature}, Transferencia: ${counts.pendingTransfer}, Pago: ${counts.pendingPayment}`,
            'info'
          );
        }
      } else {
        showSnackbar('Error al verificar notificaciones: ' + result.message, 'error');
      }
    } catch (error) {
      showSnackbar('Error al verificar notificaciones', 'error');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          <NotificationsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Configuración de Notificaciones
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={config.isActive}
              onChange={(e) => handleChange('isActive', e.target.checked)}
              color="primary"
            />
          }
          label={config.isActive ? 'Notificaciones Activas' : 'Notificaciones Inactivas'}
        />
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        El sistema verificará automáticamente la cantidad de pedidos en cada estado y enviará notificaciones
        por correo electrónico cuando se superen los umbrales configurados.
      </Alert>

      <Grid container spacing={3}>
        {/* Configuración de Pendiente de Firma */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Chip label="Pendiente de Firma" color="warning" sx={{ mr: 1 }} />
              </Box>
              
              <TextField
                fullWidth
                type="number"
                label="Umbral de Alerta"
                value={config.pendingSignatureThreshold}
                onChange={(e) => handleChange('pendingSignatureThreshold', parseInt(e.target.value) || 0)}
                error={!!errors.pendingSignatureThreshold}
                helperText={errors.pendingSignatureThreshold || 'Número de pedidos para enviar alerta'}
                sx={{ mb: 2 }}
                InputProps={{ inputProps: { min: 1 } }}
              />
              
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Emails de Notificación
                </Typography>
                {errors.pendingSignatureEmails && (
                  <Typography variant="caption" color="error">
                    {errors.pendingSignatureEmails}
                  </Typography>
                )}
              </Box>
              
              {(config.pendingSignatureEmails || ['']).map((email, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    fullWidth
                    type="email"
                    placeholder="email@ejemplo.com"
                    value={email}
                    onChange={(e) => handleEmailChange('pendingSignatureEmails', index, e.target.value)}
                    error={!!errors[`pendingSignatureEmails[${index}]`]}
                    helperText={errors[`pendingSignatureEmails[${index}]`]}
                    size="small"
                    InputProps={{
                      startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                    }}
                  />
                  {(config.pendingSignatureEmails || ['']).length > 1 && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => handleRemoveEmail('pendingSignatureEmails', index)}
                      sx={{ minWidth: 'auto', px: 1 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </Button>
                  )}
                </Box>
              ))}
              
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleAddEmail('pendingSignatureEmails')}
                fullWidth
              >
                Agregar Email
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Configuración de Pendiente de Transferencia */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Chip label="Pendiente de Transferencia" color="info" sx={{ mr: 1 }} />
              </Box>
              
              <TextField
                fullWidth
                type="number"
                label="Umbral de Alerta"
                value={config.pendingTransferThreshold}
                onChange={(e) => handleChange('pendingTransferThreshold', parseInt(e.target.value) || 0)}
                error={!!errors.pendingTransferThreshold}
                helperText={errors.pendingTransferThreshold || 'Número de pedidos para enviar alerta'}
                sx={{ mb: 2 }}
                InputProps={{ inputProps: { min: 1 } }}
              />
              
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Emails de Notificación
                </Typography>
                {errors.pendingTransferEmails && (
                  <Typography variant="caption" color="error">
                    {errors.pendingTransferEmails}
                  </Typography>
                )}
              </Box>
              
              {(config.pendingTransferEmails || ['']).map((email, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    fullWidth
                    type="email"
                    placeholder="email@ejemplo.com"
                    value={email}
                    onChange={(e) => handleEmailChange('pendingTransferEmails', index, e.target.value)}
                    error={!!errors[`pendingTransferEmails[${index}]`]}
                    helperText={errors[`pendingTransferEmails[${index}]`]}
                    size="small"
                    InputProps={{
                      startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                    }}
                  />
                  {(config.pendingTransferEmails || ['']).length > 1 && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => handleRemoveEmail('pendingTransferEmails', index)}
                      sx={{ minWidth: 'auto', px: 1 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </Button>
                  )}
                </Box>
              ))}
              
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleAddEmail('pendingTransferEmails')}
                fullWidth
              >
                Agregar Email
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Configuración de Pendiente de Pago */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Chip label="Pendiente de Pago" color="warning" sx={{ mr: 1 }} />
              </Box>
              
              <TextField
                fullWidth
                type="number"
                label="Umbral de Alerta"
                value={config.pendingPaymentThreshold}
                onChange={(e) => handleChange('pendingPaymentThreshold', parseInt(e.target.value) || 0)}
                error={!!errors.pendingPaymentThreshold}
                helperText={errors.pendingPaymentThreshold || 'Número de pedidos para enviar alerta'}
                sx={{ mb: 2 }}
                InputProps={{ inputProps: { min: 1 } }}
              />
              
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Emails de Notificación
                </Typography>
                {errors.pendingPaymentEmails && (
                  <Typography variant="caption" color="error">
                    {errors.pendingPaymentEmails}
                  </Typography>
                )}
              </Box>
              
              {(config.pendingPaymentEmails || ['']).map((email, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    fullWidth
                    type="email"
                    placeholder="email@ejemplo.com"
                    value={email}
                    onChange={(e) => handleEmailChange('pendingPaymentEmails', index, e.target.value)}
                    error={!!errors[`pendingPaymentEmails[${index}]`]}
                    helperText={errors[`pendingPaymentEmails[${index}]`]}
                    size="small"
                    InputProps={{
                      startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                    }}
                  />
                  {(config.pendingPaymentEmails || ['']).length > 1 && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => handleRemoveEmail('pendingPaymentEmails', index)}
                      sx={{ minWidth: 'auto', px: 1 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </Button>
                  )}
                </Box>
              ))}
              
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleAddEmail('pendingPaymentEmails')}
                fullWidth
              >
                Agregar Email
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Configuración General */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Configuración General
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="time"
                  label="Hora de Verificación Diaria"
                  value={config.executionTime || '09:00'}
                  onChange={(e) => handleChange('executionTime', e.target.value)}
                  error={!!errors.executionTime}
                  helperText={errors.executionTime || 'Hora del día para verificar y enviar notificaciones'}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Alert severity="info">
                  La verificación automática se ejecutará diariamente a la hora configurada.
                  También puedes probar el envío de notificaciones manualmente.
                </Alert>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={testing ? <CircularProgress size={20} /> : <PlayIcon />}
          onClick={handleTestNotifications}
          disabled={testing || saving || !config.isActive}
        >
          {testing ? 'Verificando...' : 'Probar Notificaciones'}
        </Button>
        
        <Button
          variant="contained"
          color="primary"
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || testing}
        >
          {saving ? 'Guardando...' : 'Guardar Configuración'}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Configuration;
